import React from "react";

/*
 * The order's diagnosis (careiq.n3.4, optional rec.action.orderDx; api/coverage_policy): under an order
 * to place in the Actions cell, the ICD-10 code(s) to file with it, best first, plus a short muted note on
 * why (the Medicare coverage policy, the practice's own paid-claim history, a screening rule). A
 * documentation aid: the codes come from the chart (or the screening rule), the provider chooses.
 *
 * First line (one line where it fits):
 *   choices    (newer payloads; replaces codes when present) the server-ranked pick list, ≤4:
 *              "Dx:" ("Screening Dx:" when the first choice is a screening / risk code), then the codes as
 *              small text toggles (aria-pressed; one chosen choice: a plain span), the chosen one's
 *              description, then a muted note for the chosen kind:
 *                policy     the policy link + "· paid 29/31 Medicare" when there is claim evidence
 *                paid       "Paid before for BCBS-TX PPO: 238/302" (+ " · Medicare-listed") + any policy link
 *                screening  the choice's `when` ("one-time screening")
 *                risk       "if it applies: <when>"; risk toggles are italic, an undocumented one has a
 *                           dashed underline and is NEVER chosen by default (default = the first choice
 *                           that is not an undocumented risk code); it is filed only when the provider
 *                           picks it
 *   codes      (older payloads) "Dx: I65.23 Occlusion and stenosis of bilateral carotid arteries"; 2-3
 *              codes are toggles, the first chosen; the policy link after. Rendered exactly as before.
 *   none       noCoveringDx: "No covering diagnosis documented — covered indications: <label> (<cat>), …",
 *              then "· Paid before for <payer>: <label> (<cat>) 11/11, …" when paidCategories come with it.
 *   paid       no choices but paidCategories: "No documented dx with payment history — paid before for
 *              <payer>: <label> (<cat>) 3/6, …"
 *   warning    cptEvidence.rate < 0.5: "· BCBS-TX PPO paid 17/5428 of these" appended to the line (or the
 *              line alone), e.g. venipuncture is rarely paid.
 * Second lines (muted, full width, only for detail):
 *   screening  "Hepatitis C screening: Medicare G0472 · once (…) · last 2026-03 · next eligible 2027-03",
 *              a small "Not yet eligible" tag first when flagged, the commercial codes + note on hover, the
 *              first source as a ↗ link; hidden on a sex mismatch.
 *   criteria   "PAP coverage criteria: AHI 38.5 (PSG 2025-03) ✓ · Adherence 82% … ✓ · Device CPAP …" with
 *              text status marks (✓ met, ✗ not met, ? check, — unknown, none for info), the criteria items
 *              (and note) on hover, the policy id as a ↗ link.
 *
 * The chosen code is the order's diagnosis when its ⊕ adds it (CareIQTabN3.jsx handleAdd →
 * orderDxDiagnosis). noPolicy / noCodeList with nothing new to say render nothing, as before (their legacy
 * `codes` are ignored). Calm (owner 10-06 density budget): grey meta text; the one teal is the chosen
 * code; no new colours. Never on a Follow up row (the server never serves it there), never when the
 * practice places no CareIQ orders.
 */

const withCode = (a) => (Array.isArray(a) ? a.filter((c) => c && c.code) : []);
const codesOf = (odx) => withCode(odx && odx.codes);
const choicesOf = (odx) => withCode(odx && odx.choices);
const nonEmpty = (a) => Array.isArray(a) && a.length > 0;
const RARE = 0.5;
const rarelyPaid = (odx) => {
  const e = odx && odx.cptEvidence;
  return Boolean(e && typeof e.rate === "number" && e.rate < RARE && e.of > 0);
};
const hasFlag = (s, f) => Array.isArray(s && s.flags) && s.flags.includes(f);
const screeningShown = (odx) => Boolean(odx && odx.screening && typeof odx.screening === "object" && odx.screening.label && !hasFlag(odx.screening, "sex_mismatch"));
const criteriaShown = (odx) => Boolean(odx && odx.criteria && typeof odx.criteria === "object" && odx.criteria.title);

// The list the provider picks from: `choices` when served, else the older `codes` (never for a
// noPolicy / noCodeList payload, which rendered nothing before choices existed).
const pickListOf = (odx) => {
  if (!odx) return [];
  const ch = choicesOf(odx);
  if (ch.length) return ch;
  if (odx.noPolicy || odx.noCodeList) return [];
  return codesOf(odx);
};

// The orderDx to show for a rec: null when there is nothing to say (no choices / codes, no covering-dx
// note, no paid categories, no screening or criteria detail, no rarely-paid warning); noPolicy /
// noCodeList with nothing else stays null, as before.
export const orderDxOf = (rec) => {
  const odx = rec && rec.action && rec.action.orderDx;
  if (!odx || typeof odx !== "object") return null;
  if (pickListOf(odx).length || odx.noCoveringDx || nonEmpty(odx.paidCategories) || screeningShown(odx) || criteriaShown(odx) || rarelyPaid(odx)) return odx;
  return null;
};

const isUndocRisk = (c) => c.kind === "risk" && !c.documented;
// The default: the first choice that is not an undocumented risk code (a status / history Z code is only
// filed when the provider says it applies).
const defaultOf = (list) => list.find((c) => !isUndocRisk(c)) || null;

// The chosen code: the provider's pick when it is one of the rec's choices / codes, else the default.
export const chosenDx = (rec, picked) => {
  const list = pickListOf(orderDxOf(rec));
  if (!list.length) return null;
  return list.find((c) => c.code === picked) || defaultOf(list);
};

// What /orders/create files as the order's diagnosis: the chosen code alone (dotted ICD-10, no
// description), or nothing.
export const orderDxDiagnosis = (rec, picked) => {
  const c = chosenDx(rec, picked);
  return c ? [c.code] : [];
};

const NCD = /^NCD/;
// The policy reference: the LCD when there is one ("LCD L35397"), else the article / NCD id.
export const policyRef = (odx) => {
  if (!odx) return null;
  const p = odx.policy;
  const l = odx.lcd;
  if (!p && !l) return null;
  let label;
  if (l && l.id) label = `LCD ${l.id}`;
  else if (p.type === "LCD" && !/^LCD/.test(p.id)) label = `LCD ${p.id}`;
  else if (NCD.test(p.type) && !/^NCD/.test(p.id)) label = `NCD ${p.id}`;
  else label = p.id;
  const href = (l && l.url) || (p && p.url) || null;
  const tip = [p && p.title, l && l.title && (!p || l.title !== p.title) ? l.title : null, odx.payerNote].filter(Boolean).join(" · ");
  return { label, href, tip };
};

// "Occlusion and stenosis of precerebral arteries (I65)"; "*" (any diagnosis but the NCD's
// non-supporting codes): the label alone
const indicationText = (i) => (i.category && i.category !== "*" ? `${i.label} (${i.category})` : i.label);
export const noCoverText = (odx) => {
  const ind = (odx.indications || []).filter((i) => i && i.label).map(indicationText);
  const ns = (odx.nonSpecific || []).filter((n) => n && n.code).map((n) => {
    const more = (n.coveredMoreSpecific || []).filter(Boolean);
    return `${n.code} needs a more specific code${more.length ? ` (${more.join(", ")})` : ""}`;
  });
  return [`No covering diagnosis documented${ind.length ? ` — covered indications: ${ind.join(", ")}` : ""}`, ...ns].join(" · ");
};

// "paid before for commercial plans: Malignant neoplasm of thyroid gland (C73) 3/6, …" (a category paid
// under another payer label than the first names its own)
export const paidCategoriesText = (odx) => {
  const cats = (odx && Array.isArray(odx.paidCategories) ? odx.paidCategories : []).filter((c) => c && c.label);
  if (!cats.length) return "";
  const payer = cats[0].payerLabel;
  const items = cats.map((c) => `${indicationText(c)} ${c.paid}/${c.of}${c.payerLabel && c.payerLabel !== payer ? ` ${c.payerLabel}` : ""}`);
  return `paid before for ${payer}: ${items.join(", ")}`;
};

// "BCBS-TX PPO paid 17/5428 of these" when the payer rarely pays the CPT at all
export const rarelyPaidText = (odx) => {
  if (!rarelyPaid(odx)) return "";
  const e = odx.cptEvidence;
  return `${e.payerLabel} paid ${e.paid}/${e.of} of these`;
};

const isMedicarePayer = (odx) => Boolean(odx && odx.payer && /^medicare/.test(odx.payer.class || ""));

// "Screening mammogram: Medicare 77067 · every 12 months … · last 2026-03 · next eligible 2027-03"
export const screeningText = (odx) => {
  if (!screeningShown(odx)) return "";
  const s = odx.screening;
  const m = s.medicare || {};
  const mc = (m.codes || []).filter(Boolean);
  const parts = [`${s.label}: Medicare${mc.length ? ` ${mc.join("/")}` : ""}${m.frequency ? ` · ${m.frequency}` : ""}`];
  if (m.lastDone) parts.push(`last ${m.lastDone}`);
  if (m.nextEligible) parts.push(`next eligible ${m.nextEligible}`);
  if (hasFlag(s, "outside_age_range")) parts.push("outside the usual age range");
  if (hasFlag(s, "medicare_only_service") && odx.payer && !isMedicarePayer(odx)) parts.push("Medicare-only service");
  const d = s.diagnostic;
  const dDoc = d && Array.isArray(d.documented) ? d.documented.filter(Boolean) : [];
  if (dDoc.length) parts.push(`diagnostic ${(d.codes || []).join("/")} if evaluating ${dDoc[0]}`);
  return parts.join(" · ");
};

const screeningTip = (s) => {
  const c = s.commercial || {};
  const cc = (c.codes || []).filter(Boolean);
  return [cc.length || c.note ? `Commercial${cc.length ? ` ${cc.join("/")}` : ""}${c.note ? `: ${c.note}` : ""}` : null, s.note].filter(Boolean).join(" · ");
};

const MARK = { met: "✓", not_met: "✗", check: "?", unknown: "—" };
const MARK_LABEL = { met: "met", not_met: "not met", check: "check", unknown: "unknown" };

function RefLink({ href, label, tip, sr }) {
  if (!href) return <span className="n3-odx-ref" title={tip || undefined}>{label}</span>;
  return (
    <a className="n3-odx-ref" href={href} target="_blank" rel="noopener noreferrer" title={tip || undefined} data-act="policy">
      {label}
      <span className="n3-odx-arrow" aria-hidden="true">↗</span>
      <span className="n3-sr">{` (${sr}, opens in a new tab)`}</span>
    </a>
  );
}

function PolicyLink({ odx }) {
  const ref = policyRef(odx);
  if (!ref) return null;
  return <RefLink href={ref.href} label={ref.label} tip={ref.tip} sr="coverage policy" />;
}

function ScreeningLine({ odx }) {
  const text = screeningText(odx);
  if (!text) return null;
  const s = odx.screening;
  const src = (s.sources || []).find((x) => x && x.url);
  return (
    <div className="n3-odx-l2" data-odx-line="screening">
      {hasFlag(s, "not_yet_eligible") ? <span className="n3-odx-tag">Not yet eligible</span> : null}
      <span className="n3-odx-l2-t" title={screeningTip(s) || undefined}>{text}</span>
      {src ? <RefLink href={src.url} label="source" tip={src.label} sr={src.label} /> : null}
    </div>
  );
}

function CriteriaLine({ odx }) {
  if (!criteriaShown(odx)) return null;
  const c = odx.criteria;
  const pts = (c.patient || []).filter((p) => p && p.label);
  const tip = [...(c.items || []).filter(Boolean), c.note].filter(Boolean).join(" • ");
  return (
    <div className="n3-odx-l2" data-odx-line="criteria">
      <span className="n3-odx-l2-t" title={tip || undefined}>
        {`${c.title}: `}
        {pts.map((p, i) => (
          <span key={`${p.label}-${i}`} className="n3-odx-crit" data-status={p.status}>
            {i ? " · " : ""}
            {`${p.label} ${p.value}`}
            {MARK[p.status] ? <span className="n3-odx-mark" role="img" aria-label={MARK_LABEL[p.status]}>{` ${MARK[p.status]}`}</span> : null}
          </span>
        ))}
      </span>
      {c.policy && c.policy.id ? <RefLink href={c.policy.url} label={c.policy.id} tip={c.policy.title} sr="coverage criteria" /> : null}
    </div>
  );
}

const Muted = ({ children, ev }) => <span className="n3-odx-ev" data-ev={ev}>{children}</span>;

// The muted note after the chosen choice's description
function ChoiceNote({ odx, on }) {
  const e = on.evidence;
  if (on.kind === "policy") {
    return (
      <>
        <PolicyLink odx={odx} />
        {e ? <Muted ev="paid">{`· paid ${e.paid}/${e.of} ${e.payerLabel}`}</Muted> : null}
      </>
    );
  }
  if (on.kind === "paid") {
    return (
      <>
        {e ? <Muted ev="paid">{`Paid before for ${e.payerLabel}: ${e.paid}/${e.of}${on.coveredByPolicy ? " · Medicare-listed" : ""}`}</Muted> : null}
        <PolicyLink odx={odx} />
      </>
    );
  }
  return (
    <>
      {on.when ? <Muted ev="when">{on.kind === "risk" ? `if it applies: ${on.when}` : on.when}</Muted> : null}
      <PolicyLink odx={odx} />
    </>
  );
}

const toggleCls = (c, isOn, btn = false) => `n3-odx-c${c.kind === "risk" ? " is-risk" : ""}${isUndocRisk(c) ? " is-undoc" : ""}${btn ? " no-hover-tint" : ""}${isOn ? " is-on" : ""}`;
const toggleTitle = (c) => (c.kind === "risk" && c.when ? `if it applies: ${c.when}` : c.description);

function Toggles({ rec, list, on, onPick }) {
  const onCode = on ? on.code : null;
  // one choice, chosen: plain text as before; one choice not chosen (an undocumented risk code): a toggle
  if (list.length === 1 && on) {
    return <span className={toggleCls(on, true)} data-code={on.code}>{on.code}</span>;
  }
  return (
    <span className="n3-odx-cs" role="group" aria-label="Diagnosis for this order">
      {list.map((c) => (
        <button
          key={c.code}
          type="button"
          className={toggleCls(c, c.code === onCode, true)}
          aria-pressed={c.code === onCode}
          aria-label={`${c.code} ${c.description}${c.kind === "risk" && c.when ? ` (if it applies: ${c.when})` : ""}`}
          title={toggleTitle(c)}
          data-code={c.code}
          data-kind={c.kind || undefined}
          onClick={() => onPick && onPick(rec, c.code)}
        >
          {c.code}
        </button>
      ))}
    </span>
  );
}

function Warn({ text, lead }) {
  if (!text) return null;
  return <span className="n3-odx-ev n3-odx-warn" data-ev="rare">{lead ? `· ${text}` : text}</span>;
}

const cap = (t) => (t ? `${t[0].toUpperCase()}${t.slice(1)}` : t);

export default function OrderDx({ rec, picked = null, onPick = null }) {
  const odx = orderDxOf(rec);
  if (!odx) return null;
  const choices = choicesOf(odx);
  const list = pickListOf(odx);
  const warn = rarelyPaidText(odx);
  const details = (
    <>
      <ScreeningLine odx={odx} />
      <CriteriaLine odx={odx} />
    </>
  );

  if (!list.length) {
    const paidCats = paidCategoriesText(odx);
    if (odx.noCoveringDx) {
      return (
        <div className="n3-odx is-none" data-odx="none">
          <span className="n3-odx-none">{`${noCoverText(odx)}${paidCats ? ` · ${cap(paidCats)}` : ""}`}</span>
          <PolicyLink odx={odx} />
          <Warn text={warn} lead />
          {details}
        </div>
      );
    }
    if (paidCats) {
      return (
        <div className="n3-odx is-none" data-odx="paid">
          <span className="n3-odx-none">{`No documented dx with payment history — ${paidCats}`}</span>
          <PolicyLink odx={odx} />
          <Warn text={warn} lead />
          {details}
        </div>
      );
    }
    // detail only (criteria / screening / the rarely-paid warning)
    return (
      <div className="n3-odx is-info" data-odx="info">
        <Warn text={warn} lead={false} />
        {details}
      </div>
    );
  }

  const on = chosenDx(rec, picked);
  if (!choices.length) {
    // the older codes shape, as before
    return (
      <div className="n3-odx" data-odx="codes">
        <span className="n3-odx-k">Dx:</span>
        <Toggles rec={rec} list={list} on={on} onPick={onPick} />
        <span className="n3-odx-d" title={on.description}>{on.description}</span>
        {on.needsSecondary ? <span className="n3-odx-hint">needs a secondary dx</span> : null}
        <PolicyLink odx={odx} />
        <Warn text={warn} lead />
        {details}
      </div>
    );
  }

  const screeningFirst = choices[0].kind === "screening" || choices[0].kind === "risk";
  return (
    <div className="n3-odx" data-odx="choices">
      <span className="n3-odx-k">{screeningFirst ? "Screening Dx:" : "Dx:"}</span>
      <Toggles rec={rec} list={list} on={on} onPick={onPick} />
      {on ? <span className="n3-odx-d" title={on.description}>{on.description}</span> : null}
      {on && on.needsSecondary ? <span className="n3-odx-hint">needs a secondary dx</span> : null}
      {on ? <ChoiceNote odx={odx} on={on} /> : <PolicyLink odx={odx} />}
      <Warn text={warn} lead />
      {details}
    </div>
  );
}
